import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

const CustomersGetAppPage = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  return (
    <main
      id="download"
      className="pt-16 md:pt-20"
      style={{ scrollMarginTop: '80px', fontFamily: "'Open Sans', sans-serif" }}
      data-testid="customers-get-app-page"
    >
      <Helmet>
        <title>Download — Servitium for Customers</title>
        <meta name="description" content="Hire local on real infrastructure — verified identity, real-time updates, in-app payment, and a real review record. Free for customers across the Lower Mainland." />
        <meta property="og:title" content="Download — Servitium for Customers" />
        <meta property="og:description" content="Hire local on real infrastructure — verified identity, real-time updates, in-app payment, and a real review record. Free for customers across the Lower Mainland." />
        <meta property="og:url" content="https://servitium.ca/customers/get" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download — Servitium for Customers" />
        <meta name="twitter:description" content="Hire local on real infrastructure — verified identity, real-time updates, in-app payment, and a real review record. Free for customers across the Lower Mainland." />
        <meta property="og:image" content="https://servitium.ca/og-customers.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Servitium for Customers — Hire local on real infrastructure" />
        <meta name="twitter:image" content="https://servitium.ca/og-customers.png" />
      </Helmet>
      {/* Inject animations */}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          14% { transform: scale(1.04); }
          28% { transform: scale(1); }
          42% { transform: scale(1.04); }
          56% { transform: scale(1); }
        }
      `}</style>

      <section
        className="relative py-20 md:py-28 lg:py-32 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #880808 0%, #5e0505 100%)' }}
        data-testid="customers-download-section"
      >
        {/* Radial glow behind phone area */}
        <div className="absolute top-1/2 right-[25%] -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.06] rounded-full blur-[120px] pointer-events-none hidden lg:block" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

            {/* ── LEFT COLUMN ── */}
            <div>
              {/* Eyebrow */}
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-white/60 mb-6" data-testid="customers-download-eyebrow">
                FREE ACROSS THE LOWER MAINLAND
              </p>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.08] mb-8" data-testid="customers-download-headline">
                <span className="text-white">
                  HIRE{' '}
                  <span style={{ display: 'inline-block', animation: 'heartbeat 2.5s ease-in-out infinite' }}>LOCAL</span>
                </span>
                <br />
                <span style={{ color: '#D4AF37' }}>ON A SYSTEM,</span>
                <br />
                <span className="text-white">ON ANY DEVICE.</span>
              </h1>

              {/* Body paragraph */}
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed mb-2 max-w-lg">
                Verified identity, real-time updates, in-app payment, and a real review record. Everything in one app.
              </p>
              <p
                className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-tight mb-8"
                style={{ animation: 'heartbeat 2.5s ease-in-out infinite 0.3s' }}
              >
                ALWAYS.
              </p>

              {/* Secondary line */}
              <p className="text-sm md:text-base text-white/60 mb-6">
                Choose your device to install.
              </p>

              {/* Store buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Apple App Store */}
                <a
                  href="https://apps.apple.com/ca/app/servitium/id6754441132"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-300 hover:scale-105"
                  data-testid="customers-app-store-button"
                >
                  <img src="/badge-appstore.svg" alt="Download on the App Store" className="h-[52px] w-auto" style={{ borderRadius: 0 }} />
                </a>

                {/* Google Play */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.theprojectkiss.servitium&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-300 hover:scale-105"
                  data-testid="customers-play-store-button"
                >
                  <img src="/badge-googleplay.svg" alt="Get it on Google Play" className="h-[52px] w-auto" style={{ borderRadius: 0 }} />
                </a>
              </div>

              {/* Microcopy */}
              <p className="text-sm italic text-white/60 mb-8">
                Hire on the record.
              </p>

              {/* QR block */}
              <div className="inline-flex items-center gap-4 bg-white/10 border border-white/20 backdrop-blur-md rounded-full px-5 py-3 mb-8" data-testid="customers-qr-block">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-1">
                  <img src="/qr-code.png" alt="QR Code" className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">On Desktop? Scan to install</p>
                  <p className="text-xs text-white/60">Available on iOS &amp; Android</p>
                </div>
              </div>

              {/* Bottom links */}
              <div>
                <p className="text-sm text-white/70 mb-6">
                  Not ready to download?{' '}
                  <a
                    href="https://servitium.info/talk-to-the-team"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white transition-colors"
                    data-testid="customers-talk-to-team-link"
                  >
                    Talk to our team
                  </a>
                </p>

                <div className="border-t border-white/20 pt-5">
                  <p className="text-xs text-white/50 leading-relaxed">
                    Verified businesses across the Lower Mainland. Coming to more of BC soon.
                  </p>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN: PHONE MOCKUP ── */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Background glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/[0.05] rounded-full blur-[80px] pointer-events-none" />

                {/* Phone frame */}
                <div
                  className="relative w-[280px] md:w-[300px] rounded-[3rem] p-[6px] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                  style={{ background: 'linear-gradient(145deg, #2a2a2a 0%, #111 50%, #2a2a2a 100%)' }}
                  data-testid="customers-phone-mockup"
                >
                  {/* Inner ring highlight */}
                  <div className="rounded-[2.7rem] p-[2px]" style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.15), rgba(255,255,255,0.02))' }}>
                    {/* Screen area */}
                    <div className="relative bg-black rounded-[2.6rem] overflow-hidden" style={{ aspectRatio: '9/19.5' }}>
                      {/* Customer download demo — WebM first (Chromium), MP4 fallback (Safari/iOS) */}
                      <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="customers-phone-video"
                      >
                        <source src="/customers-download.webm" type="video/webm" />
                        <source src="/customers-download.mp4" type="video/mp4" />
                      </video>

                      {/* Home indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/30 rounded-full z-20" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CustomersGetAppPage;
